import { hasFilesInDragEvent } from "@/react/utils/fileDropUtils";

const HIGHLIGHT_CLASS = "swivu-file-drop-active";

/**
 * Shared native file-drop listeners for schedule surfaces.
 * Gates on `hasFilesInDragEvent` so Bryntum EventDrag / TaskDrag keep working.
 * Attach from InstancePlugin `construct()` + `paint` — SchedulePage `addPlugins`
 * runs after first paint, so `pluginConfig.after:["render"]` never fires.
 */
export function createScheduleFileDropAttachment({ getRootElement, findDropTarget, onFilesDropped }) {
  const dragCounters = new WeakMap();
  let rootElement = null;

  const clearHighlight = (targetEl) => {
    if (!targetEl) return;
    targetEl.classList.remove(HIGHLIGHT_CLASS);
    dragCounters.delete(targetEl);
  };

  const onDragEnter = (event) => {
    if (!hasFilesInDragEvent(event)) return;

    const targetEl = findDropTarget(event.target);
    if (!targetEl) return;

    event.preventDefault();
    event.stopPropagation();

    const counter = (dragCounters.get(targetEl) || 0) + 1;
    dragCounters.set(targetEl, counter);
    if (counter === 1) {
      targetEl.classList.add(HIGHLIGHT_CLASS);
    }
  };

  const onDragOver = (event) => {
    if (!hasFilesInDragEvent(event)) return;

    // Block browser file navigation for any file drag over the root
    event.preventDefault();
    event.stopPropagation();

    const targetEl = findDropTarget(event.target);
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = targetEl ? "copy" : "none";
    }
  };

  const onDragLeave = (event) => {
    if (!hasFilesInDragEvent(event)) return;

    const targetEl = findDropTarget(event.target);
    if (!targetEl) return;

    event.preventDefault();
    event.stopPropagation();

    const counter = (dragCounters.get(targetEl) || 1) - 1;
    dragCounters.set(targetEl, counter);
    if (counter <= 0) {
      clearHighlight(targetEl);
    }
  };

  const onDrop = async (event) => {
    if (!hasFilesInDragEvent(event)) return;

    event.preventDefault();
    event.stopPropagation();

    const targetEl = findDropTarget(event.target);
    if (!targetEl) return;

    clearHighlight(targetEl);
    await onFilesDropped(event, targetEl);
  };

  const detachFrom = (el) => {
    if (!el) return;
    el.removeEventListener("dragenter", onDragEnter, false);
    el.removeEventListener("dragover", onDragOver, false);
    el.removeEventListener("dragleave", onDragLeave, false);
    el.removeEventListener("drop", onDrop, false);
  };

  const attach = () => {
    const nextRoot = getRootElement();
    if (!nextRoot) return;

    if (rootElement === nextRoot) return;

    if (rootElement && rootElement !== nextRoot) {
      detachFrom(rootElement);
    }

    rootElement = nextRoot;
    rootElement.addEventListener("dragenter", onDragEnter, false);
    rootElement.addEventListener("dragover", onDragOver, false);
    rootElement.addEventListener("dragleave", onDragLeave, false);
    rootElement.addEventListener("drop", onDrop, false);
  };

  const destroy = () => {
    detachFrom(rootElement);
    rootElement = null;
  };

  return { attach, destroy };
}
